import type { CustomOccasionInput, OccasionDefinition, OccasionSettings } from '@unipostudio/shared-types';
import { api } from './api';

export interface OccasionCatalogueFilter {
  country?: string;
  industry?: string;
  source?: 'curated' | 'imported';
  search?: string;
}

export interface OccasionCatalogue {
  data: OccasionDefinition[];
  total: number;
  /** When the importer last ran; null if it never has. */
  refreshedAt: string | null;
  countries: string[];
}

/**
 * Occasion settings and the workspace's own occasions (blueprint S20, M10,
 * M15, A11), served by apps/api/src/modules/occasions. The server stores and
 * validates definitions only; the calendar resolves each recurrence to dates
 * on the client (`shared/lib/occasions/resolve.ts`). The built-in dataset is
 * static and lives in `packages/platform-constants/src/occasions.ts`.
 */
export const occasionsService = {
  /**
   * The whole shared catalogue: curated occasions plus the public holidays
   * the server imports twice a month. Definitions only — the caller still
   * resolves each recurrence to dates.
   */
  async listCatalogue(filter: OccasionCatalogueFilter = {}): Promise<OccasionCatalogue> {
    const res = await api.get<OccasionCatalogue>('/occasions', { params: filter });
    return res.data;
  },
  /** The saved settings, or the shared defaults when the workspace never saved any. */
  async getSettings(): Promise<OccasionSettings> {
    const res = await api.get<OccasionSettings>('/workspaces/me/occasion-settings');
    return res.data;
  },

  /** Merges the patch server-side and returns the complete settings. */
  async saveSettings(patch: Partial<OccasionSettings>): Promise<OccasionSettings> {
    const res = await api.patch<OccasionSettings>('/workspaces/me/occasion-settings', patch);
    return res.data;
  },

  /** Custom occasions, oldest first, each with scope and kind `custom`. */
  async listCustom(): Promise<OccasionDefinition[]> {
    const res = await api.get<OccasionDefinition[]>('/occasions/custom');
    return res.data;
  },

  async addCustom(input: CustomOccasionInput): Promise<OccasionDefinition> {
    const res = await api.post<OccasionDefinition>('/occasions/custom', input);
    return res.data;
  },

  async updateCustom(id: string, patch: Partial<CustomOccasionInput>): Promise<OccasionDefinition> {
    const res = await api.patch<OccasionDefinition>(`/occasions/custom/${encodeURIComponent(id)}`, patch);
    return res.data;
  },

  /** The server answers 204 with no body. */
  async removeCustom(id: string): Promise<void> {
    await api.delete(`/occasions/custom/${encodeURIComponent(id)}`);
  },
};
